// Feature gate registry — RUNTIME (lean) version.
//
// This file is intentionally minimal: every gate is `name + optional Statsig
// ID type`. NO descriptions, NO surface tags, NO product metadata.
// Consumed by `lib/analytics/*` which is bundled into every page, so
// the contents land in the main client bundle.
//
// Rich metadata (description, surface) lives in `./feature-gates-meta.ts`
// — imported ONLY by the DevToolkit chunk, which is code-split via
// `next/dynamic` and never downloaded for non-internal users.
//
// Adding a new gate: add an entry here AND a matching entry in
// `feature-gates-meta.ts`. The precommit check fails if the two
// registries drift or if any gate is missing metadata.
//
// === Two-layer resolution model ===
//
// `lib/analytics/checkGate(name)` evaluates each call in this order:
//   1. DevToolkit override — any user with toolkit access can flip the
//      flag locally via the floating panel (persisted in localStorage).
//   2. Statsig — the live remote value. QA and real users see the same
//      default unless an engineer explicitly sets a local override.
//      Returns `false` when the gate is unknown or Statsig is offline.
//
// The only path to enable a flag by default is the Statsig dashboard.

export interface FeatureGate {
  /** Statsig gate name. Must match the dashboard exactly. */
  name: string;
  /**
   * Statsig unit used for assignment. Defaults to `userID`. Pre-auth surfaces
   * must use `stableID` so anonymous checks have a persistent, valid unit.
   */
  idType?: 'stableID' | 'userID';
}

export const featureGates: Record<string, FeatureGate> = {
  live_events: { name: 'web_live_events', idType: 'stableID' },
  creator_and_platform_markets: { name: 'creator_and_platform_markets', idType: 'stableID' },
  devnet_enabled: { name: 'web_devnet_enabled', idType: 'stableID' },
  mainnet_enabled: { name: 'web_mainnet_enabled' },
  proposal_voting: { name: 'web_proposal_voting', idType: 'stableID' },
  gifting: { name: 'web_gifting' },
  multi_wallets: { name: 'web_multi_wallets' },
  referrals: { name: 'web_referrals' },
  chart_candles: { name: 'web_chart_candles' },
  region: { name: 'web_region', idType: 'stableID' },
  morfian_avatar: { name: 'web_morfian_avatar' },
  rewards_platform: { name: 'web_rewards_platform', idType: 'stableID' },
  swap_trading: { name: 'web_swap_trading' },
  ai_image_generation: { name: 'web_ai_image_generation' },
};

export type FeatureGateName = keyof typeof featureGates;
